import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import {
  getAccount,
  getBotInfo,
  logout,
  type AccountProfile,
  type KycStatusEnvelope,
} from '../api/client.ts';
import { IconChevronLeft, IconLogout, IconShield, IconSupport } from '../components/icons.tsx';
import { BottomSheet } from '../components/sheet.tsx';
import { AccountLoadingSkeleton, ErrorState } from '../components/states.tsx';
import { showToast } from '../components/toast.tsx';
import { usePlatform } from '../platform/index.ts';

import { rootRoute } from './__root.tsx';

// Bot @username is effectively immutable per deploy; cache hard so the
// Support row's t.me deep-link is ready without a refetch each open.
const BOT_INFO_STALE_MS = 24 * 60 * 60 * 1000;

interface KycChipVariant {
  readonly label: string;
  readonly className: string;
}

// Per-status chip presentation. The status is locked to the
// KycStatusEnvelope union so a new upstream value cannot leak through
// without a compile error here.
const KYC_CHIP: Readonly<Record<KycStatusEnvelope, KycChipVariant>> = {
  PASSED: { label: '✓ Approved', className: 'kyc-chip kyc-chip-passed' },
  IN_PROGRESS: { label: 'In review', className: 'kyc-chip kyc-chip-progress' },
  NOT_STARTED: { label: 'Not started', className: 'kyc-chip kyc-chip-pending' },
  REJECTED: { label: 'Failed', className: 'kyc-chip kyc-chip-rejected' },
  UNKNOWN: { label: 'Unknown', className: 'kyc-chip kyc-chip-unknown' },
};

// "May 2024" formatting from the upstream's ISO timestamp. Invalid input
// (older accounts predating the createdAt column, or a string the upstream
// formatted differently) falls back to "—" so the row layout stays stable.
function formatMemberSince(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

const AccountPage = (): ReactNode => {
  const platform = usePlatform();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const accountQuery = useQuery({
    queryKey: ['account'] as const,
    queryFn: ({ signal }): Promise<AccountProfile> => getAccount(signal),
  });

  const botInfoQuery = useQuery({
    queryKey: ['bot-info'],
    queryFn: ({ signal }) => getBotInfo(signal),
    staleTime: BOT_INFO_STALE_MS,
    gcTime: BOT_INFO_STALE_MS,
    retry: 2,
  });

  const onSupport = (): void => {
    const url = botInfoQuery.data?.supportUrl;
    if (url !== undefined && url !== '') platform.openBotLink(url);
  };

  const logoutMutation = useMutation({
    mutationFn: (): Promise<{ readonly ok: true }> => logout(),
    onSuccess: (): void => {
      qc.clear();
      void navigate({ to: '/logged-out' });
    },
    onError: (): void => {
      // Surface the failure but keep the BottomSheet open so the user
      // can retry without re-tapping the destructive button on the
      // main page. The mutation is single-flight (isPending guards
      // the buttons), so a stale click does not race a re-fire.
      showToast('Logout failed. Try again.');
    },
  });

  if (accountQuery.isPending) {
    return <AccountLoadingSkeleton />;
  }
  if (accountQuery.isError) {
    return (
      <div className="screen">
        <Topbar />
        <ErrorState
          message="Couldn't load account."
          onRetry={(): void => {
            void accountQuery.refetch();
          }}
        />
      </div>
    );
  }

  const profile = accountQuery.data;
  const chip = KYC_CHIP[profile.kycStatus];

  return (
    <div className="screen">
      <Topbar />
      <main className="account-main">
        <section className="account-fields" aria-label="Account details">
          <Row label="Name" value={profile.name} />
          <Row label="Email" value={profile.email} />
          <Row label="KYC" value={<span className={chip.className}>{chip.label}</span>} />
          <Row label="Since" value={formatMemberSince(profile.memberSince)} />
        </section>

        <nav className="settings-menu" aria-label="Settings">
          <button type="button" className="settings-row" onClick={onSupport}>
            <span className="settings-row-icon" aria-hidden="true">
              <IconSupport />
            </span>
            <span className="settings-row-text">
              <span className="settings-row-label">Support</span>
              <span className="settings-row-sublabel">Chat with the support team</span>
            </span>
            <span className="settings-row-chevron" aria-hidden="true">
              ›
            </span>
          </button>
          <button
            type="button"
            className="settings-row"
            onClick={(): void => {
              void navigate({ to: '/security' });
            }}
          >
            <span className="settings-row-icon" aria-hidden="true">
              <IconShield />
            </span>
            <span className="settings-row-text">
              <span className="settings-row-label">Security</span>
              <span className="settings-row-sublabel">How this account confirms it is you</span>
            </span>
            <span className="settings-row-chevron" aria-hidden="true">
              ›
            </span>
          </button>
          <button
            type="button"
            className="settings-row settings-row-destructive"
            onClick={(): void => {
              setConfirmOpen(true);
            }}
            aria-label="Log out of this miniapp"
          >
            <span className="settings-row-icon" aria-hidden="true">
              <IconLogout />
            </span>
            <span className="settings-row-text">
              <span className="settings-row-label">Log out</span>
              <span className="settings-row-sublabel">Log out of your Mini App account</span>
            </span>
          </button>
        </nav>
      </main>

      <BottomSheet
        open={confirmOpen}
        title="Log out?"
        onClose={(): void => {
          if (!logoutMutation.isPending) setConfirmOpen(false);
        }}
      >
        <p className="confirm-body">
          You&rsquo;ll need to reopen the miniapp from chat to start a new session.
        </p>
        <div className="confirm-actions">
          <button
            type="button"
            className="sheet-btn-cancel"
            onClick={(): void => {
              setConfirmOpen(false);
            }}
            disabled={logoutMutation.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="sheet-btn-danger"
            onClick={(): void => {
              logoutMutation.mutate();
            }}
            disabled={logoutMutation.isPending}
          >
            {logoutMutation.isPending ? 'Logging out…' : 'Log out'}
          </button>
        </div>
      </BottomSheet>
    </div>
  );
};

const Topbar = (): ReactNode => {
  const navigate = useNavigate();
  return (
    <header className="topbar">
      <button
        type="button"
        className="topbar-back"
        aria-label="Back"
        onClick={(): void => {
          void navigate({ to: '/' });
        }}
      >
        <IconChevronLeft />
      </button>
      <h1 className="topbar-title">Account</h1>
      <span className="topbar-spacer" aria-hidden="true" />
    </header>
  );
};

interface RowProps {
  readonly label: string;
  readonly value: ReactNode;
  readonly mono?: boolean;
}

const Row = ({ label, value, mono = false }: RowProps): ReactNode => (
  <div className="account-row">
    <span className="account-row-label">{label}</span>
    <span className={`account-row-value${mono ? ' mono' : ''}`}>{value}</span>
  </div>
);

export const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account',
  component: AccountPage,
});
